/* Maison cart rewards bar — ported 2026-10-14 from the redesign draft (assets/mm-redesign.js, P5). */
/* P5: cart rewards bar + "Complete your kit" (cart drawer and cart page).
   <mm-rewards> renders free-shipping / free-gift progress from data set by snippets/mm-cart-rewards.liquid.
   Amounts arrive in the cart's currency (cents); an empty data-amount means "convert data-usd with Shopify.currency.rate
   and round to a clean number" (65, 80, 400, 7,000). The drawer is re-rendered by the theme on every cart change, so a new
   <mm-rewards> connects each time; the last fill is remembered so the bar animates from where it was. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Clean number, nearest (within ~5%): step = 10/25/50% of the leading power of ten by leading digit.
  // 64 → 65, 82 → 80, 175 → 180, 351 → 350, 6,750 → 7,000, 62,100 → 60,000.
  const round = (v) => { if (!(v > 0)) return 0; const k = 10 ** Math.floor(Math.log10(v)); const d = v / k; const step = Math.max(1, k * (d < 2 ? 0.1 : d < 5 ? 0.25 : 0.5)); return Math.round(v / step) * step; };
  const toCents = (usd) => round(Number(usd) * Number(window.Shopify?.currency?.rate || 1)) * 100;
  // Gift tiers from the Maison engine (data-round="up5"): USD × rate rounded UP to the next 5, exactly as the engine does.
  const up5 = (usd) => Math.ceil((Number(usd) * Number(window.Shopify?.currency?.rate || 1)) / 5) * 5 * 100;
  const amountOf = (el) => (el.dataset.amount ? Number(el.dataset.amount) : el.dataset.round === 'up5' ? up5(el.dataset.usd) : toCents(el.dataset.usd));
  function money(cents, format) {
    const fmt = format || '${{amount}}';
    const whole = cents % 100 === 0;
    const num = (precision, thousands = ',', decimal = '.') => {
      const [i, d] = (cents / 100).toFixed(precision).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (d && !whole ? decimal + d : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: num(2), amount_no_decimals: num(0), amount_with_comma_separator: num(2, '.', ','),
      amount_no_decimals_with_comma_separator: num(0, '.', ','), amount_with_apostrophe_separator: num(2, "'", '.'),
    }[k] ?? num(2)));
  }
  // R3: shared with the PDP free-shipping line (mm-pdp.js) so both use the same threshold maths.
  window.mmShip = { round, toCents, money };
  let lastLine = { drawer: 0, page: 0 }; // last fill (%) per bar, so a re-rendered drawer animates from where it was

  class MmRewards extends HTMLElement {
    connectedCallback() { this.render(); }
    render() {
      const d = this.dataset;
      const kind = this.classList.contains('mm-rewards--page') ? 'page' : 'drawer';
      const total = Number(d.total || 0);
      // Gifts measure the cart without gift products (data-gift-total), matching the Maison engine discount.
      const giftTotal = d.giftTotal != null ? Number(d.giftTotal) : total;
      const val = (s) => (s.kind === 'ship' ? total : giftTotal);
      const ship = d.ship ? Number(d.ship) : toCents(d.shipUsd || 45);
      const fmt = (c) => money(c, d.moneyFormat);
      const t = (key, amount) => (d[key] || '').replace('[amount]', amount != null ? `<strong>${fmt(amount)}</strong>` : '');
      const stops = [...this.querySelectorAll('[data-mm-stop]')].map((el) => ({
        el, kind: el.dataset.mmStop,
        amt: el.dataset.mmStop === 'ship' ? ship : amountOf(el),
      })).filter((s) => s.amt > 0).sort((a, b) => a.amt - b.amt);
      const max = stops.length ? stops[stops.length - 1].amt : ship;
      const pct = Math.max(0, Math.min(100, (total / max) * 100));
      // Milestones sit evenly along the line (last one at the end); the fill runs piecewise so it meets each marker
      // exactly when its amount is reached.
      const n = stops.length;
      const at = n === 1 ? [100] : stops.map((_, i) => 20 + (i * 80) / (n - 1)); // first reward at 20%, last at the end
      const next = stops.find((s) => val(s) < s.amt);
      stops.forEach((s, i) => {
        s.el.style.setProperty('--mm-at', `${at[i]}%`);
        s.el.classList.toggle('is-reached', val(s) >= s.amt);
        s.el.classList.toggle('is-next', s === next);
        const lab = s.el.querySelector('.mm-rewards__label');
        if (lab) lab.innerHTML = `<span class="mm-rewards__label-name">${s.kind === 'ship' ? 'Free shipping' : 'Free gift'}</span><span class="mm-rewards__label-amt">${fmt(s.amt).replace(/<[^>]*>/g, '').trim()}</span>`;
      });
      let line = 0;
      stops.forEach((s, i) => {
        const prevAmt = i ? stops[i - 1].amt : 0, prevAt = i ? at[i - 1] : 0;
        if (val(s) >= s.amt) line = at[i];
        else if (val(s) > prevAmt && line === prevAt) line = prevAt + ((val(s) - prevAmt) / (s.amt - prevAmt)) * (at[i] - prevAt);
      });
      // Gift rows: show the ones unlocked.
      this.querySelectorAll('[data-mm-gift]').forEach((g) => {
        const amt = amountOf(g);
        const warn = g.querySelector('[data-mm-warn]');
        const keep = g.dataset.inCart === 'true' && giftTotal < amt; // gift already added but the total fell below its tier
        g.hidden = giftTotal < amt && !keep;
        g.classList.toggle('is-at-risk', keep);
        if (warn) { warn.hidden = !keep; if (keep) warn.innerHTML = (g.dataset.warn || '').replace('[amount]', `<strong>${fmt(amt - giftTotal)}</strong>`); }
      });
      const note = this.querySelector('.mm-rewards__note');
      if (note) note.innerHTML = next
        ? (total === 0 ? t('tShipEmpty', next.amt) : t(next.kind === 'ship' ? 'tShipAway' : 'tGiftAway', next.amt - val(next)))
        : (stops.some((s) => s.kind === 'gift') ? "You've unlocked free shipping and your gifts" : t('tShipDone'));
      this.classList.toggle('is-complete', !next);
      const track = this.querySelector('.mm-rewards__track');
      if (!track) return;
      track.setAttribute('aria-valuenow', String(Math.round(pct)));
      const fill = track.querySelector('.mm-rewards__fill');
      if (!fill) return;
      const from = lastLine[kind];
      lastLine[kind] = line;
      if (reduce) { fill.style.transform = `scaleX(${line / 100})`; return; }
      fill.style.transition = 'none';
      fill.style.transform = `scaleX(${from / 100})`;
      requestAnimationFrame(() => requestAnimationFrame(() => { fill.style.transition = ''; fill.style.transform = `scaleX(${line / 100})`; }));
    }
  }
  if (!customElements.get('mm-rewards')) customElements.define('mm-rewards', MmRewards);
})();
